import { Loader, Popover, Stack, Text, UnstyledButton } from "@mantine/core";
import type { CSSProperties } from "react";

import { useGetMemberAchievements } from "../../api/generated/endpoints/achievements/achievements";
import type { MemberAchievements as Shared } from "../../api/generated/model";
import { Badge } from "../achievements/Badge";
import { TIER_GLYPH, TIER_NAME, tierColor } from "../achievements/words";
import { ErrorAlert } from "../ErrorAlert";
import { ROOM_EITHER } from "../../ui/popover-room";
import classes from "./MemberAchievements.module.css";

/** What a member who shares in this team earned in it, newest first (spec web/achievements, 5). */
function Earned({ teamId, userId }: { teamId: string; userId: string }) {
  const list = useGetMemberAchievements(teamId, userId);
  if (list.isError) return <ErrorAlert error={list.error} />;
  if (list.data === undefined) return <Loader size="xs" aria-label="Loading their achievements" />;
  return (
    <Stack gap={10}>
      {list.data.map((each) => (
        <div key={each.id} className={classes.row} style={{ "--tier": tierColor(each.tier) } as CSSProperties}>
          <Badge tier={each.tier} glyph={each.glyph} state="earned" size={32} />
          <div>
            <Text size="sm" fw={600}>
              {each.name} <span className={classes.tier}>{TIER_NAME[each.tier]}</span>
            </Text>
            <Text size="xs">{each.description}</Text>
          </div>
        </div>
      ))}
    </Stack>
  );
}

/**
 * Beside the name of a member who shares in this team (spec web/achievements, 5): their best
 * tier's badge and how many they earned here, opening the list. A member who does not share, an
 * admin included, shows nothing (decision 1.9).
 */
export function MemberAchievements({ teamId, name, shared }: { teamId: string; name: string; shared: Shared }) {
  const best = shared.best_tier;
  const words = `${name}'s achievements here: ${shared.earned}${best === null ? "" : `, best ${TIER_NAME[best]}`}`;
  return (
    // The room on its side bounds the list, which scrolls in it (decision 23).
    <Popover width={320} position="bottom-start" shadow="md" middlewares={ROOM_EITHER} classNames={{ dropdown: classes.list }}>
      <Popover.Target>
        <UnstyledButton className={classes.chip} aria-label={words}>
          {best === null ? null : <Badge tier={best} glyph={TIER_GLYPH[best]} state="earned" size={20} />}
          {shared.earned}
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown>
        <Earned teamId={teamId} userId={shared.user_id} />
      </Popover.Dropdown>
    </Popover>
  );
}
